import type {
  EnumKey,
  EnumValue,
  ObjVal,
} from '@repo/internal-tools-type-utils/src/utils.ts'
import { cva } from 'class-variance-authority'
import type {
  StyleGetterProps,
  StyleGetterTypes,
} from '../../theme/styleGetter.ts'
import {
  ICON_SIZES,
  type IconSize,
} from '../IconContainer/IconContainer.styles.ts'
import {
  COMPONENT_DIRECTION,
  type TypographySizeEnumKey,
  type TypographySizeEnumValue,
} from '../variants.ts'

export const CONTENT_SIZES = {
  DISPLAY_XL: 'display-xl',
  DISPLAY_LG: 'display-lg',
  DISPLAY_MD: 'display-md',
  DISPLAY_SM: 'display-sm',
  HEADINGS_H1: 'headings-h1',
  HEADINGS_H2: 'headings-h2',
  '2XL': '2xl',
  XL: 'xl',
  LG: 'lg',
  MD: 'md',
  SM: 'sm',
  XS: 'xs',
} satisfies Record<TypographySizeEnumKey, TypographySizeEnumValue>
export const DEFAULT_CONTENT_SIZE = CONTENT_SIZES.MD

export type ContentSizeConfig = typeof CONTENT_SIZES
export type ContentSize = ObjVal<ContentSizeConfig>

export const CONTENT_ALIGNS = {
  LEFT: 'left',
  CENTER: 'center',
  RIGHT: 'right',
} satisfies Record<EnumKey, EnumValue>
export const DEFAULT_CONTENT_ALIGN = CONTENT_ALIGNS.LEFT

export type ContentAlignConfig = typeof CONTENT_ALIGNS
export type ContentAlign = ObjVal<ContentAlignConfig>

export const DEFAULT_CONTENT_DIRECTION = COMPONENT_DIRECTION.VERTICAL

// min-w-0 is to prevent itself from overflowing the parent
export const getContentRootLayoutStyle = cva('min-w-0', {
  variants: {
    size: {
      [CONTENT_SIZES.DISPLAY_XL]: undefined,
      [CONTENT_SIZES.DISPLAY_LG]: undefined,
      [CONTENT_SIZES.DISPLAY_MD]: undefined,
      [CONTENT_SIZES.DISPLAY_SM]: undefined,
      [CONTENT_SIZES.HEADINGS_H1]: undefined,
      [CONTENT_SIZES.HEADINGS_H2]: undefined,
      [CONTENT_SIZES['2XL']]: undefined,
      [CONTENT_SIZES.XL]: undefined,
      [CONTENT_SIZES.LG]: undefined,
      [CONTENT_SIZES.MD]: undefined,
      [CONTENT_SIZES.SM]: undefined,
      [CONTENT_SIZES.XS]: undefined,
    },
    direction: {
      [COMPONENT_DIRECTION.VERTICAL]: 'block',
      [COMPONENT_DIRECTION.HORIZONTAL]: 'flex',
    },
  },
  compoundVariants: [
    {
      direction: COMPONENT_DIRECTION.VERTICAL,
      size: CONTENT_SIZES.DISPLAY_XL,
      className: 'space-y-space-gap-xl',
    },
    {
      direction: COMPONENT_DIRECTION.VERTICAL,
      size: CONTENT_SIZES.DISPLAY_LG,
      className: 'space-y-space-gap-xl',
    },
    {
      direction: COMPONENT_DIRECTION.VERTICAL,
      size: CONTENT_SIZES.DISPLAY_MD,
      className: 'space-y-space-gap-md',
    },
    {
      direction: COMPONENT_DIRECTION.VERTICAL,
      size: CONTENT_SIZES.DISPLAY_SM,
      className: 'space-y-space-gap-md',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.DISPLAY_XL,
      className: 'gap-space-gap-xl',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.DISPLAY_LG,
      className: 'gap-space-gap-xl',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.DISPLAY_MD,
      className: 'gap-space-gap-xl',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.DISPLAY_SM,
      className: 'gap-space-gap-xl',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.HEADINGS_H1,
      className: 'gap-space-gap-lg',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.HEADINGS_H2,
      className: 'gap-space-gap-lg',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES['2XL'],
      className: 'gap-space-gap-lg',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.XL,
      className: 'gap-space-gap-lg',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.LG,
      className: 'gap-space-gap-md',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.MD,
      className: 'gap-space-gap-md',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.SM,
      className: 'gap-space-gap-md',
    },
    {
      direction: COMPONENT_DIRECTION.HORIZONTAL,
      size: CONTENT_SIZES.XS,
      className: 'gap-space-gap-sm',
    },
  ],
  defaultVariants: {
    size: DEFAULT_CONTENT_SIZE,
    direction: DEFAULT_CONTENT_DIRECTION,
  },
})

type GetContentRootLayoutStyle = typeof getContentRootLayoutStyle
export type GetContentRootLayoutStyleProps =
  StyleGetterProps<GetContentRootLayoutStyle>

export const getContentElementContainerLayoutStyle = cva(
  'gap-space-gap-2xs flex items-center [&>.icon-container]:shrink-0',
  {
    variants: {
      align: {
        // this doesn't do anything atm because label will have flex-1 but it's here as a fallback
        [CONTENT_ALIGNS.LEFT]: 'justify-between',
        [CONTENT_ALIGNS.CENTER]: 'justify-center',
        // this doesn't do anything atm because label will have flex-1 but it's here as a fallback
        [CONTENT_ALIGNS.RIGHT]: 'justify-end',
      },
    },
    defaultVariants: {
      align: DEFAULT_CONTENT_ALIGN,
    },
  },
)

type GetContentElementContainerLayoutStyle =
  typeof getContentElementContainerLayoutStyle

export type GetContentElementContainerLayoutStyleTypes =
  StyleGetterTypes<GetContentElementContainerLayoutStyle>
export type GetContentElementContainerLayoutStyleProps =
  StyleGetterProps<GetContentElementContainerLayoutStyle>

export const getContentLabelLayoutStyle = cva('block', {
  variants: {
    font: {
      base: undefined,
      // Not supporting `marketing` yet since there's no use case for it now.
      // marketing: undefined,
    },
    size: {
      [CONTENT_SIZES.DISPLAY_XL]: undefined,
      [CONTENT_SIZES.DISPLAY_LG]: undefined,
      [CONTENT_SIZES.DISPLAY_MD]: undefined,
      [CONTENT_SIZES.DISPLAY_SM]: undefined,
      [CONTENT_SIZES.HEADINGS_H1]: undefined,
      [CONTENT_SIZES.HEADINGS_H2]: undefined,
      [CONTENT_SIZES['2XL']]: undefined,
      [CONTENT_SIZES.XL]: undefined,
      [CONTENT_SIZES.LG]: undefined,
      [CONTENT_SIZES.MD]: undefined,
      [CONTENT_SIZES.SM]: undefined,
      [CONTENT_SIZES.XS]: undefined,
    },
    align: {
      [CONTENT_ALIGNS.LEFT]: 'flex-1 text-left',
      [CONTENT_ALIGNS.CENTER]: 'text-center',
      [CONTENT_ALIGNS.RIGHT]: 'flex-1 text-right',
    },
    shouldWrapLabel: {
      true: 'text-pretty wrap-anywhere min-w-0',
      false: 'truncate',
    },
  },
  compoundVariants: [
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_XL,
      className: 'typo-base-display-xl',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_LG,
      className: 'typo-base-display-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_MD,
      className: 'typo-base-display-md',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_SM,
      className: 'typo-base-display-sm',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.HEADINGS_H1,
      className: 'typo-base-headings-h1',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.HEADINGS_H2,
      className: 'typo-base-headings-h2',
    },
    {
      font: 'base',
      size: CONTENT_SIZES['2XL'],
      className: 'typo-base-headings-h2',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.XL,
      className: 'typo-base-headings-h3',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.LG,
      className: 'typo-base-labels-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.MD,
      className: 'typo-base-labels-md',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.SM,
      className: 'typo-base-labels-sm',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.XS,
      className: 'typo-base-labels-xs',
    },
  ],
  defaultVariants: {
    font: 'base',
    size: DEFAULT_CONTENT_SIZE,
    align: DEFAULT_CONTENT_ALIGN,
    shouldWrapLabel: true,
  },
})

type GetContentLabelLayoutStyle = typeof getContentLabelLayoutStyle

export type GetContentLabelLayoutStyleTypes =
  StyleGetterTypes<GetContentLabelLayoutStyle>
export type GetContentLabelLayoutStyleProps =
  StyleGetterProps<GetContentLabelLayoutStyle>

export const getContentDescriptionContainerLayoutStyle = cva(undefined, {
  variants: {
    direction: {
      [COMPONENT_DIRECTION.VERTICAL]: undefined,
      [COMPONENT_DIRECTION.HORIZONTAL]: 'flex-1 min-w-0',
    },
  },
  defaultVariants: {
    direction: DEFAULT_CONTENT_DIRECTION,
  },
})

type GetContentDescriptionContainerLayoutStyle =
  typeof getContentDescriptionContainerLayoutStyle
export type GetContentDescriptionContainerLayoutStyleProps =
  StyleGetterProps<GetContentDescriptionContainerLayoutStyle>

export const getContentDescriptionStateStyle = cva(undefined, {
  variants: {
    hasIssue: {
      false: undefined,
      true: 'text-foreground-potential-issue',
    },
  },
  defaultVariants: {
    hasIssue: false,
  },
})

type GetContentDescriptionStateStyle = typeof getContentDescriptionStateStyle

export type GetContentDescriptionStateStyleTypes =
  StyleGetterTypes<GetContentDescriptionStateStyle>
export type GetContentDescriptionStateStyleProps =
  StyleGetterProps<GetContentDescriptionStateStyle>

export const getContentDescriptionLayoutStyle = cva(undefined, {
  variants: {
    font: {
      base: undefined,
      // Not supporting `marketing` yet since there's no use case for it now.
      // marketing: undefined,
    },
    size: {
      [CONTENT_SIZES.DISPLAY_XL]: undefined,
      [CONTENT_SIZES.DISPLAY_LG]: undefined,
      [CONTENT_SIZES.DISPLAY_MD]: undefined,
      [CONTENT_SIZES.DISPLAY_SM]: undefined,
      [CONTENT_SIZES.HEADINGS_H1]: undefined,
      [CONTENT_SIZES.HEADINGS_H2]: undefined,
      [CONTENT_SIZES['2XL']]: undefined,
      [CONTENT_SIZES.XL]: undefined,
      [CONTENT_SIZES.LG]: undefined,
      [CONTENT_SIZES.MD]: undefined,
      [CONTENT_SIZES.SM]: undefined,
      [CONTENT_SIZES.XS]: undefined,
    },
    align: {
      [CONTENT_ALIGNS.LEFT]: 'flex-1 text-left',
      [CONTENT_ALIGNS.CENTER]: 'text-center',
      [CONTENT_ALIGNS.RIGHT]: 'flex-1 text-right',
    },
    shouldWrapDescription: {
      true: 'text-pretty wrap-anywhere min-w-0 whitespace-pre-line',
      false: 'truncate',
    },
  },
  compoundVariants: [
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_XL,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_LG,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_MD,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.DISPLAY_SM,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.HEADINGS_H1,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.HEADINGS_H2,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES['2XL'],
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.XL,
      className: 'typo-base-text-lg',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.LG,
      className: 'typo-base-text-md',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.MD,
      className: 'typo-base-text-sm',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.SM,
      className: 'typo-base-text-xs',
    },
    {
      font: 'base',
      size: CONTENT_SIZES.XS,
      className: 'typo-base-text-xs',
    },
  ],
  defaultVariants: {
    font: 'base',
    size: DEFAULT_CONTENT_SIZE,
    align: DEFAULT_CONTENT_ALIGN,
    shouldWrapDescription: true,
  },
})

type GetContentDescriptionLayoutStyle = typeof getContentDescriptionLayoutStyle
export type GetContentDescriptionLayoutStyleProps =
  StyleGetterProps<GetContentDescriptionLayoutStyle>

export const LABEL_ICON_SIZE_BY_CONTENT_SIZE = {
  [CONTENT_SIZES.DISPLAY_XL]: ICON_SIZES['2XL'],
  [CONTENT_SIZES.DISPLAY_LG]: ICON_SIZES['2XL'],
  [CONTENT_SIZES.DISPLAY_MD]: ICON_SIZES['2XL'],
  [CONTENT_SIZES.DISPLAY_SM]: ICON_SIZES['2XL'],
  [CONTENT_SIZES.HEADINGS_H1]: ICON_SIZES['2XL'],
  [CONTENT_SIZES.HEADINGS_H2]: ICON_SIZES['2XL'],
  [CONTENT_SIZES['2XL']]: ICON_SIZES['2XL'],
  [CONTENT_SIZES.XL]: ICON_SIZES.XL,
  [CONTENT_SIZES.LG]: ICON_SIZES.LG,
  [CONTENT_SIZES.MD]: ICON_SIZES.MD,
  [CONTENT_SIZES.SM]: ICON_SIZES.SM,
  [CONTENT_SIZES.XS]: ICON_SIZES.XS,
} satisfies Record<ContentSize, IconSize>

export const getContentLabelIconSizeByContentSize = (
  size: ContentSize | null | undefined,
) => {
  return LABEL_ICON_SIZE_BY_CONTENT_SIZE[size ?? DEFAULT_CONTENT_SIZE]
}

export const DESCRIPTION_ICON_SIZE_BY_CONTENT_SIZE = {
  [CONTENT_SIZES.DISPLAY_XL]: ICON_SIZES.LG,
  [CONTENT_SIZES.DISPLAY_LG]: ICON_SIZES.LG,
  [CONTENT_SIZES.DISPLAY_MD]: ICON_SIZES.LG,
  [CONTENT_SIZES.DISPLAY_SM]: ICON_SIZES.LG,
  [CONTENT_SIZES.HEADINGS_H1]: ICON_SIZES.LG,
  [CONTENT_SIZES.HEADINGS_H2]: ICON_SIZES.LG,
  [CONTENT_SIZES['2XL']]: ICON_SIZES.LG,
  [CONTENT_SIZES.XL]: ICON_SIZES.LG,
  [CONTENT_SIZES.LG]: ICON_SIZES.MD,
  [CONTENT_SIZES.MD]: ICON_SIZES.SM,
  [CONTENT_SIZES.SM]: ICON_SIZES.XS,
  [CONTENT_SIZES.XS]: ICON_SIZES.XS,
} satisfies Record<ContentSize, IconSize>

export const getContentDescriptionIconSizeByContentSize = (
  size: ContentSize | null | undefined,
) => {
  return DESCRIPTION_ICON_SIZE_BY_CONTENT_SIZE[size ?? DEFAULT_CONTENT_SIZE]
}
